import { createStyles, Theme } from '@material-ui/core/styles';

import { colours } from '@shared/theme/colours';

// The footer is floored to a fixed height so the panel does not jump when switching
// between tabs whose notes wrap to different line counts.
const FOOTER_MIN_HEIGHT = 65;

export const styles = ({ spacing }: Theme) =>
  createStyles({
    root: {
      background: colours.solid.white,
      display: 'flex',
      flexDirection: 'column',
      // The popover paper is a flex column capped to the viewport, so this lets the panel
      // shrink to fit it and keep the footer link on screen for shorter viewports.
      minHeight: 0,
      width: '100%',
    },
    header: {
      alignItems: 'center',
      display: 'flex',
      gap: 8,
      padding: spacing(3.5, 4),
    },
    headerTitle: {
      color: colours.neutral[800],
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '24px',
    },
    divider: {
      background: colours.neutral[200],
      flexShrink: 0,
      height: 1,
      width: '100%',
    },
    tabs: {
      display: 'flex',
      gap: 20,
      padding: spacing(0, 4),
    },
    tab: {
      alignItems: 'center',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      color: colours.neutral[500],
      cursor: 'pointer',
      display: 'inline-flex',
      fontFamily: 'inherit',
      fontSize: 14,
      fontWeight: 500,
      gap: 6,
      padding: spacing(2.5, 0),
    },
    tabActive: {
      borderBottomColor: colours.neutral[900],
      color: colours.neutral[900],
      fontWeight: 600,
    },
    tabDot: {
      background: colours.brandSalmon[500],
      borderRadius: '50%',
      display: 'block',
      flexShrink: 0,
      height: 6,
      width: 6,
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    // 550 is the preferred height (keeps the panel's size consistent across tabs), but it
    // can shrink so the header, tabs and footer stay on screen on shorter viewports.
    // minHeight: 0 lets it shrink past its content size so the list scrolls instead.
    tabPanel: {
      display: 'flex',
      flex: '0 1 550px',
      flexDirection: 'column',
      minHeight: 0,
      overflowY: 'auto',
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
    },
    loadMoreContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      padding: spacing(3, 0),
    },
    footer: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      justifyContent: 'center',
      minHeight: FOOTER_MIN_HEIGHT,
      padding: spacing(3, 4),
    },
    skeletonRow: {
      borderBottom: `1px solid ${colours.neutral[200]}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      padding: 16,
    },
    skeletonTitle: {
      background: colours.neutral[100],
      borderRadius: 4,
      height: 12,
      width: 150,
    },
    skeletonMessage: {
      background: colours.neutral[100],
      borderRadius: 4,
      height: 12,
      width: 240,
    },
    skeletonTime: {
      background: colours.neutral[100],
      borderRadius: 4,
      height: 12,
      width: 64,
    },
    empty: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: 8,
      justifyContent: 'center',
      padding: spacing(9, 6),
      textAlign: 'center',
    },
    emptyIcon: {
      fontSize: 24,
    },
    emptyTitle: {
      color: colours.neutral[800],
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '20px',
    },
    emptyText: {
      color: colours.neutral[500],
      fontSize: 13,
      fontWeight: 400,
      lineHeight: '18px',
    },
    // Neutral counterpart to markAll: a footer action that is not the primary one for the
    // tab, so it does not compete with the salmon mark-all.
    footerNote: {
      color: colours.neutral[400],
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '18px',
    },
    footerLink: {
      alignItems: 'center',
      background: 'none',
      border: 'none',
      color: colours.brandNavy[600],
      cursor: 'pointer',
      display: 'inline-flex',
      fontFamily: 'inherit',
      fontSize: 13,
      fontWeight: 600,
      gap: 6,
      padding: 0,
    },
  });
